import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import { Calendar } from 'primereact/calendar';
import { InputSwitch } from 'primereact/inputswitch';
import { Skeleton } from 'primereact/skeleton';
import styled from 'styled-components';
import { isNotBlank } from '@utils/utils';
import { shortDateFormat, stringIsoFromDateInTz, stringYMDFromDate } from '@utils/dateWithTz';
import AddonsSvg from '@app/icons/AddonsSvg.tsx';
import { toBoolean } from '@app/pages/customer/supplier-notification/utils.ts';
import { ExpandSvg } from '@icons/ExpandSvg';
import SvgClose from '@icons/close.svg?react';
import SvgSave from '@icons/save.svg?react';
import { DateTimeFormatType, FilterBarProps, FilterType, isFilterObjectActive, OptionType } from './types';
import { FilterTag } from './FilterTag';
import { TreeFilterChip } from './v2/TreeFilterChip';

/**
 * The original filter UI: a responsive grid of inputs with a "show more filters" expander.
 * Superseded by the v2 chip bar ({@link FilterBarV2}) but still selectable per user.
 */
export const LegacyFilterBar = <TFilter,>({
  filtersConf,
  filter,
  applyFilterValue,
  applyFilter,
  savedFilters = [],
  savingEnabled,
  loading,
  isMobile,
  expandedFilters,
  setExpandedFilters,
  onSaveFilter,
  onClearFilter,
  onManageFilters,
}: FilterBarProps<TFilter>) => {
  const { tr } = useTranslationLgs();
  const { i18n } = useTranslation();

  const getCalendarValue = (fromDate?: string, toDate?: string) => {
    const dates = [];
    if (fromDate) {
      dates.push(new Date(fromDate));
    }
    if (toDate) {
      dates.push(new Date(toDate));
    }
    return dates.length > 0 ? dates : null;
  };

  const handleCalendarChange = (
    e: any,
    onFromChange: (value: string | null) => void,
    onToChange: (value: string | null) => void,
    dateTimeFormat: DateTimeFormatType,
  ) => {
    const formatType = dateTimeFormat ?? DateTimeFormatType.DateString;

    if (!e.value || !Array.isArray(e.value)) {
      onFromChange(null);
      if (onToChange) {
        onToChange(null);
      }
      return;
    }

    const dateArray = e.value.filter((date: Date | null) => date !== null);

    if (dateArray.length >= 1) {
      const fromValue =
        formatType === DateTimeFormatType.ISOString
          ? stringIsoFromDateInTz(dateArray[0], 'Europe/Prague')
          : stringYMDFromDate(dateArray[0]);
      onFromChange(fromValue);
    }
    if (dateArray.length >= 2) {
      const toValue =
        formatType === DateTimeFormatType.ISOString
          ? stringIsoFromDateInTz(dateArray[1], 'Europe/Prague')
          : stringYMDFromDate(dateArray[1]);
      onToChange(toValue);
    }
    if (dateArray.length === 1 && onToChange) {
      onToChange(null);
    }
  };

  // Shared option rendering for MultiSelect / Dropdown filters — supports an optional
  // per-option icon (color swatch, country flag, transport icon) and the inactive line-through.
  const renderOption = (option: OptionType) => (
    <span className={clsx('inline-flex items-center gap-2', !option.active && 'line-through text-content-tertiary')}>
      {option.icon}
      {option.label}
    </span>
  );

  const filters = !filtersConf ? [] : filtersConf.map(conf => {
    switch (conf.filterType) {
      case FilterType.InputText: {
        const filterKey = conf.value[0];
        const filterValue = filter && typeof filter === 'object' && filterKey in filter
          ? (filter as any)[filterKey]
          : '';

        return (
          <div className="field" key={filterKey}>
            <div className="relative w-full">
              <InputText
                value={filterValue || ''}
                onChange={e => {
                  const { value } = e.target as HTMLInputElement;
                  applyFilterValue(filterKey, value);
                }}
                placeholder={conf.placeholder}
                className="w-full"
                style={{ paddingRight: isNotBlank(filterValue) ? '2.2rem' : undefined }}
                data-testid={conf.dataTestId}
              />
              {isNotBlank(filterValue) && (
                <i
                  className="pi pi-times"
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    color: 'var(--ringil3-brand-color)',
                  }}
                  onClick={() => applyFilterValue(filterKey, '')}
                />
              )}
            </div>
          </div>
        );
      }
      case FilterType.MultiSelect: {
        const filterKey = conf.value[0];
        const filterValue: Array<{ label: string; value: string; active: boolean }> =
          filter && typeof filter === 'object' && filterKey in filter
            ? (filter as any)[filterKey] as Array<{ label: string; value: string; active: boolean }>
            : [];

        return (
          <div className="field" key={filterKey}>
            <MultiSelect
              filter={true}
              value={Array.isArray(filterValue) ? filterValue : []}
              options={conf.options}
              onChange={e => {
                applyFilterValue(filterKey, e.value);
              }}
              showClear={true}
              optionLabel="label"
              optionValue="value"
              itemTemplate={(option: OptionType) => renderOption(option)}
              placeholder={conf.placeholder}
            />
          </div>
        );
      }
      case FilterType.MultiSelectLazy: {
        const filterKey = conf.value[0];
        const filterValue: Array<{ label: string; value: string; active: boolean }> =
          filter && typeof filter === 'object' && filterKey in filter
            ? (filter as any)[filterKey] as Array<{ label: string; value: string; active: boolean }>
            : [];

        return (
          <div className="field" key={filterKey}>
            <MultiSelect
              filter={true}
              value={Array.isArray(filterValue) ? filterValue : []}
              options={conf.options}
              onChange={e => {
                applyFilterValue(filterKey, e.value);
              }}
              showClear={true}
              optionLabel="label"
              optionValue="value"
              itemTemplate={(option: OptionType) => renderOption(option)}
              placeholder={conf.placeholder}
              virtualScrollerOptions={{
                lazy: true,
                onLazyLoad: conf.onLazyLoad,
                itemSize: 38,
                showLoader: true,
                loading: loading,
                loadingTemplate: (options: any) => (
                  <div style={{ height: '50px' }}>
                    <Skeleton width={options.even ? '60%' : '50%'} height="1.3rem" />
                  </div>
                ),
              }}
            />
          </div>
        );
      }
      // DropdownLazy renders here without its virtual scroller: the legacy bar shows whatever window
      // the owner has loaded rather than nothing at all (`default` below returns null).
      case FilterType.Dropdown:
      case FilterType.DropdownLazy: {
        const filterKey = conf.value[0];
        const filterValue = filter && typeof filter === 'object' && filterKey in filter
          ? (filter as any)[filterKey]
          : '';

        return (
          <div className="field" key={filterKey}>
            <Dropdown
              value={filterValue}
              options={conf.options}
              onChange={e => {
                applyFilterValue(filterKey, e.value);
              }}
              optionLabel="label"
              optionValue="value"
              itemTemplate={(option: OptionType) => renderOption(option)}
              valueTemplate={(option: OptionType) => (option ? renderOption(option) : <span>{conf.placeholder}</span>)}
              placeholder={conf.placeholder}
              className="w-full"
              filter
              showClear={isNotBlank(filterValue)}
            />
          </div>
        );
      }
      case FilterType.Calendar: {
        const filterValues = Array.isArray(conf.value)
          ? conf.value.map(key => filter && typeof filter === 'object' && key in filter ? (filter as any)[key] : null)
          : [
            filter && typeof filter === 'object' && conf.value in filter
              ? (filter as any)[conf.value]
              : null,
          ];

        // A single-key value renders a single date picker; two keys render a range.
        const isSingleDate = conf.value.length === 1;
        const formatSingleDate = (date: Date | null) => {
          if (!date) return null;
          // Use the shared date helpers: stringYMDFromDate keeps the picked day's local calendar
          // components (a calendar day has no timezone — this was the "shifted by one day" regression).
          return conf.dateTimeFormat === DateTimeFormatType.ISOString
            ? stringIsoFromDateInTz(date, 'Europe/Prague')
            : stringYMDFromDate(date);
        };

        return (
          <div
            className={`${isNotBlank(filterValues[0]) ? 'calendar-has-value' : ''} relative w-full`}
            key={Array.isArray(conf.value) ? conf.value.join('-') : conf.value}
          >
            <Calendar
              locale={i18n.language}
              selectionMode={isSingleDate ? 'single' : 'range'}
              value={isSingleDate ? (filterValues[0] ? new Date(filterValues[0]) : null) : getCalendarValue(filterValues[0], filterValues[1])}
              onChange={e =>
                isSingleDate
                  ? applyFilterValue(conf.value[0], formatSingleDate(e.value as Date | null))
                  : handleCalendarChange(
                    e,
                    value => applyFilterValue(conf.value[0], value),
                    value => applyFilterValue(conf.value[1], value),
                    conf.dateTimeFormat,
                  )
              }
              readOnlyInput
              dateFormat={shortDateFormat}
              showIcon
              icon={<AddonsSvg width={20} height={20} />}
              showButtonBar={true}
              placeholder={conf.placeholder}

            />
            {isNotBlank(filterValues[0]) && (
              <i
                className="pi pi-times"
                style={{
                  position: 'absolute',
                  right: '2.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  cursor: 'pointer',
                  color: 'var(--ringil3-brand-color)',
                  zIndex: 2,
                }}
                onClick={() => {
                  applyFilterValue(conf.value[0], null);
                  if (Array.isArray(conf.value) && conf.value.length === 2) {
                    applyFilterValue(conf.value[1], null);
                  }
                }}
              />
            )}
          </div>
        );
      }
      case FilterType.TreeMultiSelect: {
        // The legacy grid has no tree widget of its own — reuse the v2 chip so a table configured with
        // a tree filter still shows it here instead of silently dropping the field.
        return (
          <div className="field flex items-center" key={conf.value.join('-')}>
            <TreeFilterChip conf={conf} filter={filter} applyFilterValue={applyFilterValue} loading={loading} />
          </div>
        );
      }
      case FilterType.Checkbox: {
        const filterKey = conf.value[0];
        const filterValue = filter && typeof filter === 'object' && filterKey in filter
          ? (filter as any)[filterKey]
          : false;

        return (
          <div className={'flex items-center gap-2'} key={filterKey}>
            <InputSwitch className="expedition-filters-switch" checked={toBoolean(filterValue)}
                         onChange={e => applyFilterValue(filterKey, e.value)} />
            <label>{conf.placeholder}</label>
          </div>
        );
      }
      default:
        return null;
    }
  });

  return (
    <div className={clsx('font-[\'DM Sans\']', isMobile && '-mx-4 px-4 pt-4')}>
      {/* Saved Filters Row */}
      {savedFilters.length > 0 && (
        <div className="flex items-center gap-2">
          <div className="flex flex-wrap gap-2">
            {savedFilters.map(savedFilter => (
              <FilterTag
                key={savedFilter.id}
                label={savedFilter.name}
                onClick={() => {
                  // Apply the RAW saved filter; normalization happens once at fetch time.
                  applyFilter(savedFilter.data);
                  if (Object.values(savedFilter.data).some(value => value !== null && value !== '' && (!Array.isArray(value) || value.length > 0))) {
                    setExpandedFilters(true);
                  }
                }}
              />
            ))}
          </div>
          {savedFilters.length > 0 && (
            <Button icon="pi pi-pencil" onClick={onManageFilters}
                    className="p-button-text !text-orange-500 hover:!text-orange-600" />
          )}
        </div>
      )}

      {filters && filters.length > 0 && (
        <div className="filters">
          <FilterRow>
            <div className="primary-filters">
              {(() => {
                let visibleFields;
                if (isMobile && !expandedFilters) {
                  visibleFields = filters.slice(0, 4);
                } else if (!isMobile && !expandedFilters) {
                  visibleFields = filters.slice(0, 7);
                } else {
                  visibleFields = filters;
                }
                return visibleFields;
              })()}

              <div className={`col-span-full flex items-center ${isMobile ? (filters.length > 4 ? 'justify-end' : 'justify-between') : (filters.length > 7 ? 'justify-between' : 'justify-end')}`}>
                {(isMobile ? filters.length > 4 : filters.length > 7) && (
                  <Button
                    label={expandedFilters ? tr('RingilDataTable.hideFilters', 'Hide filters') : tr('RingilDataTable.showFilters', 'Show filters')}
                    icon={<ExpandSvg />}
                    onClick={() => setExpandedFilters(!expandedFilters)}
                    className="p-button-text !text-[#fe5000] hover:!text-orange-600 !inline-flex !items-center"
                  />
                )}
                <div className="flex items-center justify-end">
                  {savingEnabled && (
                    <div
                      className="flex flex-row items-center justify-center gap-1 cursor-pointer text-content-brand hover:bg-content-brandMedium"
                      onClick={onSaveFilter}
                    >
                      <SvgSave />
                      {tr(`RingilDataTable.saveFilter`, `Save`)}
                    </div>
                  )}
                  {isFilterObjectActive(filter) && (
                    <>
                      <div className="inline-block w-[1px] h-5 bg-content-tertiary mx-1.5 align-middle" />
                      <div
                        className="flex flex-row items-center justify-center gap-1 cursor-pointer text-content-brand hover:bg-content-brandMedium"
                        onClick={onClearFilter}
                      >
                        <SvgClose />
                        {tr(`RingilDataTable.clearFilter`, `Clear`)}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </FilterRow>
        </div>
      )}
    </div>
  );
};

export const FilterRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: 1rem 0;
  font-family: 'DM Sans', sans-serif;

  .primary-filters,
  .additional-filters {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.5rem 1rem;
    width: 100%;

    @media (max-width: 1280px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 768px) {
      grid-template-columns: repeat(1, minmax(0, 1fr));
    }

    &.hidden {
      display: none;
    }

    .field {
      min-width: 200px;

      @media (max-width: 768px) {
        width: 100%;
        min-width: 100%;
      }

      .p-inputtext,
      .p-calendar,
      .p-multiselect,
      .p-dropdown {
        width: 100%;
      }
    }

    .field-checkbox {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .dates-group {
      display: flex;
      gap: 0.5rem;
      grid-column: span 1;

      @media (max-width: 1280px) {
        grid-column: span 1;
      }

      @media (max-width: 768px) {
        grid-column: span 1;
        flex-direction: row;
      }

      .field {
        min-width: unset;
        flex: 1;
      }
    }
  }
`;
